import React, { useMemo } from "react";
import PrintController from "./PrintController";
import Logo from "./components/Logo";
import Title from "./components/Title";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import PrintText from "./components/PrintText";
import Hr from "./components/Hr";
import { setPrintaction } from "../../../../redux/printstate/printActions";
import LetterHead from "./components/LetterHead";
import PrintDate from "./components/PrintDate";
import { PrintSettingsContext } from "./PrintSettingsContext";
import PrintSettingsShell from "./PrintSettingsShell";
import "./printcontroller.scss";

const formatPrintSubTreatmentLabel = (items: any) => {
  const parts = [items?.treatment || "N/A"];
  if (items?.type) parts.push(items.type);
  if (items?.subtype) parts.push(items.subtype);
  if (items?.stage) parts.push(items.stage);
  return parts.join(" ");
};

const isLongTermTreatment = (item: any) => item?.status === "long term";

const shouldShowPrintSubTreatment = (items: any, invoiceNo?: string) =>
  !!items &&
  !isLongTermTreatment(items) &&
  items.subInvoice === invoiceNo &&
  (items.selected_option === "treatment_paid" ||
    items.selected_option === "treatment_stage" ||
    items.selected_option === "treatment_non_paid");

type PrinttestProps = {
  viewsetting?: boolean;
  stateKey?: "print" | "printTemplate2";
  saveApi?: string;
  grabApi?: string;
  printAction?: typeof setPrintaction;
};

function Printtest({
  viewsetting,
  stateKey = "print",
  saveApi = "/clinic/addBillData",
  grabApi = "/clinic/addBillDataGrabPosition",
  printAction = setPrintaction,
}: PrinttestProps) {
  const isPrintTemplate2 = stateKey === "printTemplate2";
  const {
    printPosition,
    clinic,
    patient,
    treatmentdata,
    amount,
    subTreatmentdata,
  } = useSelector((state: RootState) => state[stateKey]);

  const longTermDue = amount?.longTermDue ?? 0;
  const estimation = amount?.estimation ?? 0;
  const hasLongTermSummary = estimation > 0 || longTermDue > 0;

  const age = useMemo(() => {
    if (!patient?.dob) return 0;
    const birthdateDate = new Date(patient?.dob);
    const currentDate = new Date(patient?.createdAt ?? new Date());

    const ageDiff = currentDate.getFullYear() - birthdateDate.getFullYear();
    const isBirthdayPassed =
      currentDate.getMonth() < birthdateDate.getMonth() ||
      (currentDate.getMonth() === birthdateDate.getMonth() &&
        currentDate.getDate() < birthdateDate.getDate());
    return ageDiff - (isBirthdayPassed ? 1 : 0);
  }, [patient?.dob, patient?.createdAt]);

  const printableTreatments = useMemo(
    () => treatmentdata?.filter((item: any) => !isLongTermTreatment(item)) ?? [],
    [treatmentdata]
  );

const dates = new Date(patient?.invoiceDate);

 
const options: Intl.DateTimeFormatOptions = {
  timeZone: "Asia/Colombo", // GMT+5:30
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  hour12: true,
};
const localTime = dates
    .toLocaleString("en-GB", options)
    .replace(/\b(a\.?m\.?|p\.?m\.?)\b/gi, (m) => (m.startsWith("p") ? "PM" : "AM"))
    .replace(", ", "|");

  const preview = (
    <div
      style={{
        background: "white",
        width: "100%",
        height: "auto",
      }}
      className="billtext"
    >
      <div
        style={{
          border: printPosition?.container?.show ? "1px solid black" : 0,
          width: printPosition?.container?.left,
          overflow: "hidden",
          padding: 10,
          position: "relative",
          height: printPosition?.container?.top,
          background: "white",
        }}
      >
        {printPosition?.logo?.show && (
          <Logo item={printPosition?.logo} src={clinic?.printImages} />
        )}
        {printPosition?.footerImage?.show && (
          <LetterHead
            item={printPosition?.footerImage}
            src={clinic?.footerImages}
          />
        )}
        {printPosition?.headerImage?.show && (
          <LetterHead
            item={printPosition?.headerImage}
            src={clinic?.headerImages}
          />
        )}

          <Title item={printPosition?.header} clinicName={clinic?.clinicName} />
          <PrintText item={printPosition?.invoiceNotext} title={"invoice No"} />
          <PrintText
            item={printPosition?.invoiceNo}
            title={patient?.invoiceNo}
          />
          <PrintText item={printPosition?.datetimetext} title={"Date/Time"} />
          <PrintDate
            item={printPosition?.datetime}
            title={localTime}
          />
          <PrintText item={printPosition?.regNotext} title={"Regitration No"} />

          <PrintText item={printPosition?.regNo} title={patient?.regNo} />
          <PrintText
            item={printPosition?.patientNametext}
            title={"Patient Name"}
          />
          <PrintText
            item={printPosition?.patientName}
            title={patient?.fullName}
          />
          {age > 0 && <PrintText item={printPosition?.agetext} title={"age"} />}
          {age > 0 && (
            <PrintText item={printPosition?.age} title={age ? age : 0} />
          )}
          <PrintText
            item={printPosition?.contactNotext}
            title={"Cont. No."}
          />
          <PrintText
            item={printPosition?.contactNo}
            title={patient?.mobileNo}
          />
          <PrintText item={printPosition?.gendertext} title={"Gender"} />
          <PrintText item={printPosition?.gender} title={patient?.gender} />
          <Hr item={printPosition?.horizontallineone} />
          <table
            className={isPrintTemplate2 ? "treatmentbill treatmentbill-template2" : "treatmentbill"}
            style={{
              position: "absolute",
              top: printPosition?.treatmentDetail?.top,
              left: printPosition?.treatmentDetail?.left,
              width: printPosition?.treatmentDetail?.size,
              display: printPosition?.treatmentDetail?.show ? "" : "none",
            }}
          >
            {isPrintTemplate2 && (
              <colgroup>
                <col style={{ width: "75%" }} />
                <col style={{ width: "25%" }} />
              </colgroup>
            )}
            <thead>
              <tr>
                {!isPrintTemplate2 && <th>No</th>}
                <th>Treatment</th>
                {!isPrintTemplate2 && <th>Rate</th>}
                {!isPrintTemplate2 && <th>Units</th>}
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let index = 1;
                return (
                  <>
                    {printableTreatments.map((items: any, i: number) => (
                      <tr key={`treatment-${i}`}>
                        {!isPrintTemplate2 && (
                          <td style={{ width: 40, textAlign: "center" }}>
                            {index++}
                          </td>
                        )}
                        <td>{items?.treatment}</td>
                        {!isPrintTemplate2 && (
                          <>
                            <td style={{ textAlign: "center" }}>
                              Rs. {items?.price / items?.quantity}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {items?.quantity}
                            </td>
                          </>
                        )}
                        <td>Rs. {items?.price}</td>
                      </tr>
                    ))}
                    {subTreatmentdata?.map((items: any, i: number) => {
                      if (!shouldShowPrintSubTreatment(items, patient?.invoiceNo)) {
                        return null;
                      }
                      const quantity = items?.quantity || 1;
                      const price = parseFloat(items?.price) || 0;
                      return (
                        <tr key={`subtreatment-${i}`}>
                          {!isPrintTemplate2 && (
                            <td style={{ width: 40, textAlign: "center" }}>
                              {index++}
                            </td>
                          )}
                          <td>{formatPrintSubTreatmentLabel(items)}</td>
                          {!isPrintTemplate2 && (
                            <>
                              <td style={{ textAlign: "center" }}>
                                Rs. {price / quantity}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {quantity}
                              </td>
                            </>
                          )}
                          <td>Rs. {price}</td>
                        </tr>
                      );
                    })}
                  </>
                );
              })()}
            </tbody>
          </table>
          <div className="dots"></div>
          <table
            className="amountbill"
            style={{
              position: "absolute",
              top: printPosition?.billDetails?.top,
              left: printPosition?.billDetails?.left,
              width: printPosition?.billDetails?.size,
              display: printPosition?.billDetails?.show ? "" : "none",
        
            }}
          >
            <thead></thead>
            <tbody>
              <tr>
                <td>Sub Total</td>
                <td>Rs. {amount?.total}</td>
              </tr>

              <tr>
                <td>Last due</td>
                <td>Rs. {amount?.lastDue}</td>
              </tr>
              <tr>
                <td>Discount</td>
                <td>Rs. {amount?.discount}</td>
              </tr>
              <tr className="total">
                <td>Total</td>
                <td>Rs. {amount?.netotal}</td>
              </tr>
              <tr>
                <td colSpan={2}>
                  <div className="dots"></div>
                </td>
              </tr>

              <tr>
                <td>Card</td>
                <td>Rs. {amount?.card}</td>
              </tr>
              <tr>
                <td>Cash</td>
                <td>Rs. {amount?.cash}</td>
              </tr>
              <tr>
                <td>Bank Transfer</td>
                <td>Rs. {amount?.bankTransfer ? amount?.bankTransfer : 0}</td>
              </tr>

              <tr className="total">
                <td>
                  {amount?.netotal -
                    (amount?.card + amount?.cash + amount?.bankTransfer) >
                  0
                    ? "current due"
                    : "current advance"}
                </td>
                <td>
                  Rs. {Math.abs(
                    amount?.netotal -
                      (amount?.card + amount?.cash + amount?.bankTransfer)
                      ? amount?.netotal -
                          (amount?.card + amount?.cash + amount?.bankTransfer)
                      : 0
                  )}
                </td>
              </tr>
              {hasLongTermSummary && (
                <>
                  <tr className="longterm-divider">
                    <td colSpan={2}>
                      <div className="longterm-hr"></div>
                    </td>
                  </tr>
                  <tr className="longterm-row">
                    <td>Estimation</td>
                    <td>Rs. {estimation}</td>
                  </tr>
                  <tr className="longterm-row">
                    <td>Long Term Due</td>
                    <td>Rs. {longTermDue}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
          <Hr item={printPosition?.horizontallinetwo} />
          <PrintText item={printPosition?.addresstext} title={"address :"} />
          <PrintText item={printPosition?.address} title={clinic?.address} />
          <PrintText item={printPosition?.teltext} title={"Tel :"} />
          <PrintText item={printPosition?.tel} title={clinic?.tpNo} />
          <PrintText
            item={printPosition?.dentisttext}
            title={"Dental Surgeon :"}
          />
        <PrintText item={printPosition?.dentist} title={clinic?.drName} />
      </div>
    </div>
  );

  return (
    <PrintSettingsContext.Provider value={{ printAction, grabApi }}>
      {viewsetting ? (
        <PrintSettingsShell
          preview={preview}
          previewLabel={isPrintTemplate2 ? "Bill Template 2 Preview" : "Bill Preview"}
          controls={
            <PrintController
              item={printPosition}
              api={saveApi}
              action={printAction}
            />
          }
        />
      ) : (
        preview
      )}
    </PrintSettingsContext.Provider>
  );
}

export default Printtest;
